import { Anchor, Button, PinInput } from '@mantine/core'

import { formatCooldown } from 'src/components/DriverAuth/smsSendLimit'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

interface OtpStepProps {
  phone: string
  otp: string
  loading: boolean
  /** Waiting on the Cloudflare check before a resend can go out. */
  verifying?: boolean
  cooldownSeconds: number
  onOtpChange: (value: string) => void
  onSubmit: () => void
  onResend: () => void
  onEdit: () => void
}

const OtpStep = ({
  phone,
  otp,
  loading,
  verifying = false,
  cooldownSeconds,
  onOtpChange,
  onSubmit,
  onResend,
  onEdit,
}: OtpStepProps) => (
  <div className="flex flex-col items-center gap-4">
    <div className="text-center">
      <div className="text-2xl font-semibold text-doubleNickel-gray-900">
        Verify your phone number
      </div>
      <div className="mt-1 text-sm text-doubleNickel-gray-600">
        Enter the 6-digit code we texted to{' '}
        <span className="whitespace-nowrap font-medium text-doubleNickel-gray-800">
          {formatInternationalPhoneNumber(phone) || phone}
        </span>
        .{' '}
        <button
          type="button"
          onClick={onEdit}
          disabled={loading}
          className="font-medium text-doubleNickel-brand-500 hover:underline disabled:cursor-not-allowed disabled:text-doubleNickel-gray-400 disabled:no-underline"
        >
          Edit
        </button>
      </div>
    </div>

    <form
      className="flex w-full flex-col items-center gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!loading && otp.length === 6) onSubmit()
      }}
    >
      <PinInput
        length={6}
        size="md"
        placeholder=""
        type="number"
        inputType="tel"
        oneTimeCode
        // Intentional: focus the first code box when the verify step opens.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={otp}
        onChange={onOtpChange}
      />

      <Button
        fullWidth
        size="md"
        type="submit"
        loading={loading}
        disabled={otp.length < 6}
      >
        Verify
      </Button>
    </form>

    <div className="text-sm text-doubleNickel-gray-600">
      Didn&apos;t get it?{' '}
      <Anchor
        component="button"
        type="button"
        onClick={onResend}
        disabled={loading || cooldownSeconds > 0}
        className="disabled:cursor-not-allowed disabled:text-doubleNickel-gray-400 disabled:no-underline"
      >
        {cooldownSeconds > 0
          ? `Resend in ${formatCooldown(cooldownSeconds)}`
          : 'Resend code'}
      </Anchor>
    </div>

    {verifying && (
      <div role="status" className="text-xs text-doubleNickel-gray-500">
        Verifying your browser…
      </div>
    )}
  </div>
)

export default OtpStep
